// Minimal MSE-over-WebSocket player for go2rtc streams.
//
// Protocol (go2rtc /api/ws): on open we send {type:"mse", value:"<codecs>"}
// listing the codecs THIS browser can decode. go2rtc replies with
// {type:"mse", value:"video/mp4; codecs=\"...\""} and then streams binary
// fMP4 (an init segment followed by media segments) which we append to a
// single SourceBuffer. Because we only advertise codecs the browser actually
// supports, go2rtc transcodes H.265->H.264 for Firefox / non-HEVC Chrome and
// passes HEVC through untouched where the browser can play it.

// Codec strings we probe for. Order doesn't matter; go2rtc picks the best the
// browser claims. Including hvc1/hev1 lets HEVC-capable clients avoid a
// transcode; avc1 covers everyone else.
const CANDIDATE_CODECS = [
  "avc1.640029", "avc1.640028", "avc1.4d402a", "avc1.42e01e", // H.264
  "hvc1.1.6.L153.B0", "hev1.1.6.L153.B0",                     // H.265
  "mp4a.40.2",                                                 // AAC-LC
];

// Trim playback buffer so a long-running cell doesn't grow without bound.
const MAX_BUFFER_S = 60;
const TRIM_KEEP_S = 15;
// If playback drifts more than this behind the live edge, jump forward.
const LIVE_DRIFT_S = 2.0;
// Cap the pre-SourceBuffer queue so a stuck codec negotiation can't grow
// memory without bound (we drop the oldest segments).
const MAX_QUEUE_BYTES = 16 * 1024 * 1024;
// Reconnect backoff: exponential with jitter, capped — so a permanently
// down camera doesn't hammer the backend/go2rtc every 2s forever.
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export type StatusCb = (status: "connecting" | "live" | "error", detail?: string) => void;

export class MsePlayer {
  private video: HTMLVideoElement;
  private url: string;
  private onStatus: StatusCb;

  private ws: WebSocket | null = null;
  private ms: MediaSource | null = null;
  private sb: SourceBuffer | null = null;
  private mime = "";
  private queue: ArrayBuffer[] = [];
  private queuedBytes = 0;
  private attempt = 0;
  private stopped = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(video: HTMLVideoElement, url: string, onStatus: StatusCb) {
    this.video = video;
    this.url = url;
    this.onStatus = onStatus;
  }

  start(): void {
    if (typeof MediaSource === "undefined") {
      this.onStatus("error", "MSE not supported in this browser");
      return;
    }
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.teardown();
  }

  private supportedCodecs(): string {
    return CANDIDATE_CODECS
      .filter((c) => MediaSource.isTypeSupported(`video/mp4; codecs="${c}"`))
      .join(",");
  }

  private connect(): void {
    this.onStatus("connecting");
    this.queue = [];
    this.queuedBytes = 0;
    this.mime = "";

    const ms = new MediaSource();
    this.ms = ms;
    this.video.src = URL.createObjectURL(ms);
    this.video.muted = true; // required for autoplay; cameras have no audio anyway

    ms.addEventListener("sourceopen", () => {
      // Free the object URL now that the MediaSource is attached.
      if (this.video.src.startsWith("blob:")) URL.revokeObjectURL(this.video.src);
      this.maybeCreateSourceBuffer();
    });

    const codecs = this.supportedCodecs();
    const ws = new WebSocket(this.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.addEventListener("open", () => {
      ws.send(JSON.stringify({ type: "mse", value: codecs }));
    });

    ws.addEventListener("message", (ev) => {
      if (typeof ev.data === "string") {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "mse" && typeof msg.value === "string") {
            this.mime = msg.value;
            this.maybeCreateSourceBuffer();
          }
        } catch { /* ignore non-JSON control frames */ }
        return;
      }
      const buf = ev.data as ArrayBuffer;
      this.queue.push(buf);
      this.queuedBytes += buf.byteLength;
      // If the SourceBuffer never gets created (stuck negotiation), bound the
      // backlog by dropping the oldest segments rather than growing forever.
      while (!this.sb && this.queuedBytes > MAX_QUEUE_BYTES && this.queue.length > 1) {
        const dropped = this.queue.shift()!;
        this.queuedBytes -= dropped.byteLength;
      }
      this.flush();
    });

    ws.addEventListener("close", () => this.scheduleReconnect());
    ws.addEventListener("error", () => this.scheduleReconnect());
  }

  private maybeCreateSourceBuffer(): void {
    if (this.sb || !this.mime || !this.ms || this.ms.readyState !== "open") return;
    try {
      const sb = this.ms.addSourceBuffer(this.mime);
      sb.mode = "segments";
      sb.addEventListener("updateend", () => {
        this.trim();
        this.liveSync();
        this.flush();
      });
      this.sb = sb;
      this.flush();
    } catch (e) {
      // A codec mismatch won't fix itself on reconnect — surface it and free
      // the socket/queue instead of leaving the connection accumulating bytes.
      this.onStatus("error", `codec ${this.mime} not supported`);
      this.stop();
    }
  }

  private flush(): void {
    const sb = this.sb;
    if (!sb || sb.updating || this.queue.length === 0) return;
    const chunk = this.queue.shift()!;
    this.queuedBytes -= chunk.byteLength;
    try {
      sb.appendBuffer(chunk);
    } catch (e) {
      if (e instanceof DOMException && e.name === "QuotaExceededError") {
        // Buffer full — drop the oldest data and retry on the next updateend.
        this.queue.unshift(chunk);
        this.queuedBytes += chunk.byteLength;
        this.trim(true);
      } else {
        this.onStatus("error", String(e));
      }
    }
  }

  private trim(force = false): void {
    const sb = this.sb;
    if (!sb || sb.updating || sb.buffered.length === 0) return;
    const start = sb.buffered.start(0);
    const end = sb.buffered.end(sb.buffered.length - 1);
    if (force || end - start > MAX_BUFFER_S) {
      const removeTo = Math.min(end - TRIM_KEEP_S, this.video.currentTime - 1);
      if (removeTo > start) {
        try { sb.remove(start, removeTo); } catch { /* ignore */ }
      }
    }
  }

  private liveSync(): void {
    const sb = this.sb;
    if (!sb || sb.buffered.length === 0) return;
    this.attempt = 0; // data is flowing — reset reconnect backoff
    const liveEdge = sb.buffered.end(sb.buffered.length - 1);
    if (this.video.currentTime === 0 || liveEdge - this.video.currentTime > LIVE_DRIFT_S) {
      this.video.currentTime = Math.max(0, liveEdge - 0.3);
    }
    if (this.video.paused) {
      this.video.play().then(() => this.onStatus("live")).catch(() => { /* gesture */ });
    } else {
      this.onStatus("live");
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    this.teardown();
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.attempt)
      + Math.floor(Math.random() * 500);
    this.attempt++;
    this.onStatus("connecting", `reconnecting… (try ${this.attempt})`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this.connect();
    }, delay);
  }

  private teardown(): void {
    if (this.ws) {
      try { this.ws.onclose = null; this.ws.onerror = null; this.ws.close(); } catch { /* */ }
      this.ws = null;
    }
    this.sb = null;
    if (this.ms && this.ms.readyState === "open") {
      try { this.ms.endOfStream(); } catch { /* */ }
    }
    this.ms = null;
    this.queue = [];
    this.queuedBytes = 0;
    try { this.video.removeAttribute("src"); this.video.load(); } catch { /* */ }
  }
}
